import { readonly, ref } from 'vue';

// Userflow anchor inspection mode (KP-14417, S-0006): outlines every
// [data-kp-anchor] of the screen, so that the business picks a maintained
// target without DevTools. It is turned on and off from the URL
// (`?kp-anchors=on|off`, see kpAnchorsGuard) or the console
// (`window.kpAnchors.enable()`), and the state lives in sessionStorage to
// survive in-app navigation and reloads of the tab. Module-level singleton:
// the app has one inspection mode.
const KEY = 'kp-anchors'; // URL parameter and sessionStorage key

// sessionStorage may be missing or throw (privacy settings, sandboxed frame):
// the mode then lasts for the current page only.
function readStored() {
  try {
    return window.sessionStorage.getItem(KEY) === 'on';
  } catch {
    return false;
  }
}

function store(active) {
  try {
    if (active) {
      window.sessionStorage.setItem(KEY, 'on');
    } else {
      window.sessionStorage.removeItem(KEY);
    }
  } catch {
    // Not persisted: see readStored.
  }
}

const isActive = ref(readStored());

function setActive(active) {
  isActive.value = active;
  store(active);
}

const enable = () => setActive(true);
const disable = () => setActive(false);

window.kpAnchors = { enable, disable };

/**
 * Router guard applying `?kp-anchors=on|off` when a navigation changes it, the
 * first one included. Registered before `auth`, it reads the URL a redirect to
 * the login page would drop. Any other value leaves the mode as it is, and so
 * does a screen rewriting its query with the parameter unchanged.
 */
export function kpAnchorsGuard(to, from) {
  const value = to.query[KEY];

  if (value === from.query[KEY]) {
    return;
  }
  if (value === 'on') {
    enable();
  } else if (value === 'off') {
    disable();
  }
}

export default function useKpAnchorsInspection() {
  return { isActive: readonly(isActive), enable, disable };
}
